// TODO: resolve naming for FE-specific code
import React, { Fragment, useContext, useEffect, useState } from 'react';
import { Typography, useMediaQuery, useTheme } from '@material-ui/core';
import { Button } from '@michelin/acid-components';
import { useTranslation } from '@michelin/central-provider';
import { OrderDetailContext } from 'compositions/OrderDetail/OrderDetailContext';
import {
  getGroupedOptions,
  getGroupedTiresFromOrder,
} from 'compositions/Wizard/SelectTiresAndParts/MountingOption/getGroupedTires';
import { useCurrentLocationIsDealer } from 'hooks/useCurrentLocationIsDealer';
import { useGetAdditionalServices } from 'hooks/useGetAdditionalServices';
import hash from 'object-hash';
import { OrderType } from 'types/OrderType';
import { Part } from 'types/UFOTypes';
import { MAXIMUM_MOUNTED_WHEELS, TIRE_DEFAULT_QTY } from 'utils/constantValues';
import { getMspn } from 'utils/getMspn';
import { CommonPricesParamsType } from 'utils/getPricesCallCommonParams';
import { BREAKPOINT } from 'utils/responsivenessUtils';
import ChangeTires from './ChangeTires';
import EditableParts from './EditableParts';
import { Limits } from './Limits';
import { MountingOptionsModalOrderDetail } from './MountingOptionsModalOrderDetail';
import Totals from './Totals';
import { isInstallation } from './isInstallation';

interface PartsAndServicesProps {
  order: OrderType;
  isEditing?: boolean;
  setNewOrder?: (param: OrderType) => void;
  setParts?: (parts: any[]) => void;
  printMode?: boolean;
  commonPricesParams: CommonPricesParamsType;
  editedOrder?: boolean;
  componentPlacement: string;
  oldOrder?: boolean;
  smallSize?: boolean;
}

export default function PartsAndServices(props: PartsAndServicesProps) {
  const {
    order,
    isEditing = false,
    printMode = false,
    setNewOrder,
    setParts,
    commonPricesParams,
    editedOrder,
    componentPlacement,
    oldOrder,
    smallSize,
  } = props;
  const { t } = useTranslation();
  const theme = useTheme();
  const mobileView = useMediaQuery(theme.breakpoints.down(BREAKPOINT));
  const [changeTiresDialog, setChangeTiresDialog] = useState(false);
  const isDealer = useCurrentLocationIsDealer();
  const { partsAreEdited, setVehicleIsEdited, vehicleIsEdited, mountingOptionsModalOpen, setMountingOptionsModalOpen } =
    useContext(OrderDetailContext);

  useEffect(() => {
    setChangeTiresDialog(vehicleIsEdited);
  }, [vehicleIsEdited]);

  const additionalServicesResp = useGetAdditionalServices({
    with_service_mspn: true,
    fleet_id: order.orderer_location_core.customer_number,
    dealer_id: order.provider_location_core.customer_number,
    isMobile: order.additional_information.appointment_type === "is_mobile",
  });

  const additionalServices = additionalServicesResp.data?.addlServices || [];

  // eslint-disable-next-line no-underscore-dangle
  let parts: Part[] = order.parts
    .filter(
      (p) =>
        // eslint-disable-next-line no-underscore-dangle
        (!p.deleted && p.selected !== false && !p._destroy) ||
        p.unselectedInstallation ||
        p.noAddedInstallation !== undefined,
    )
    .map((p) => ({ ...p, selected: p.selected !== undefined ? p.selected : true }));

  const tiresCount = parts?.filter((p) => p.subtype === 'TIRE').reduce((count, tire) => (!tire.removed ? count + (tire.quantity || 0) : count), 0) || 0;

  const installations = order.parts.filter((p) => isInstallation(getMspn(p) as string));
  const stockingGroupedParts = getGroupedOptions(getGroupedTiresFromOrder(order.parts));
  const stockingOrder = !order.vehicle.id;
  const areProductsAdded = parts.some(
    (part) => !isInstallation(getMspn(part) as string) && (part.part_id || part.mspn),
  );

  // Remove duplicated tires
  if (stockingOrder)
    parts = parts.filter(
      (p1, index) =>
        index === parts.findIndex((p2) => p1.mspn === p2.mspn) ||
        isInstallation(getMspn(p1) as string) ||
        p1.type !== 'PRODUCTS', // only filter TIRES
    );

  const setSelectedPart = (editedPart: Part, isTire: boolean) => {
    if (isTire && setParts) {
      // Is Tire
      setParts(
        order.parts.map((p: Part) => {
          // Change edited part
          if (
            (p.id && p.id === editedPart.id) ||
            (!p.id && getMspn(p) === getMspn(editedPart) && editedPart.related_to === p.related_to)
          ) {
            return { ...p, selected: p.selected === undefined ? false : !p.selected };
          }
          // Change related parts when edit Tire
          if (editedPart.subtype === 'TIRE' && p.related_to === editedPart.related_to) {
            return { ...p, selected: editedPart.selected === undefined ? false : !editedPart.selected };
          }
          return p;
        }),
      );
    }
    if (!isTire && setParts) {
      // Is service
      setParts(
        order.parts.map((p: Part) => {
          if (
            (p.id && p.id === editedPart.id) ||
            (!p.id &&
              getMspn(p) === getMspn(editedPart) &&
              editedPart.related_to === p.related_to &&
              p.lineIndex !== undefined &&
              p.lineIndex === editedPart.lineIndex)
          ) {
            return {
              ...p,
              selected: p.selected === undefined ? false : !p.selected,
              _destroy: !p.selected,
            };
          }
          return p;
        }),
      );
    }
    if (editedPart.noAddedInstallation && setParts) {
      const newParts = JSON.parse(JSON.stringify(order.parts)) as Part[];
      newParts.push({ ...editedPart, selected: true, noAddedInstallation: false });
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      setParts(newParts);
    }
  };

  const service = (part: Part) => {
    return {
      ...additionalServices.find((as) => getMspn(as) === getMspn(part)),
      ...part,
      // TODO: should ispql be is_pql?
      pql_pricing: part.pql_pricing || part.ispql,
    };
  };

  const setPartsFunction = (editedParts: Part[]) => {
    const editableParts = JSON.parse(JSON.stringify(order.parts)) as Part[];
    editedParts.forEach((editedPart) => {
      let index = -1;
      if (editedPart.id) index = editableParts?.findIndex((p) => p.id === editedPart.id);
      else if (editedPart.related_to)
        index = editableParts?.findIndex((p) => p.mspn === editedPart.mspn && p.related_to === editedPart.related_to);
      else if (editedPart.lineIndex)
        index = editableParts?.findIndex((p) => p.mspn === editedPart.mspn && p.lineIndex === editedPart.lineIndex);
      if (index === undefined || index === -1) return;
      editableParts[index] = editedPart;
    });
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    if (setParts) setParts(editableParts);
  };

  return (
    <>
      {mountingOptionsModalOpen && setParts && (
        <MountingOptionsModalOrderDetail
          open
          order={order}
          onClose={() => setMountingOptionsModalOpen(null)}
          maxValue={MAXIMUM_MOUNTED_WHEELS}
          defaultValue={TIRE_DEFAULT_QTY}
          setParts={setParts}
          commonPricesParams={commonPricesParams}
        />
      )}
      {isEditing && (
        <Button
          color="primary"
          onClick={() => setChangeTiresDialog(true)}
          size="small"
          fullWidth
          style={{ marginBottom: mobileView ? 8 : 16 }}
        >
          {t('Change tires and parts')}
        </Button>
      )}
      {isEditing && partsAreEdited && (
        <Limits
          fleet_id={order?.orderer_id ?? ''}
          provider={order?.provider_location_core}
          orderApprovalLimit={order?.approval_limit}
          parts={parts.map((p: Part) => ({
            part_id: getMspn(p) as string,
            quantity: p.quantity || Number(p.qty),
            part_description: p.description,
            ispql: p.pql_pricing || false,
            invoice_price: p.invoice_price ? Number.parseFloat(p.invoice_price) : undefined,
            final_invoice_price: p.final_invoice_price ? Number.parseFloat(p.final_invoice_price) : undefined,
            manual_pricing: p.manual_pricing,
          }))}
          commonPricesParams={commonPricesParams}
        />
      )}
      {!areProductsAdded && (
        <Typography align="center" style={{ fontSize: 20 }}>
          {t('Please add a product')}
        </Typography>
      )}
      {changeTiresDialog && (
        <ChangeTires
          order={order}
          dialog={changeTiresDialog}
          setDialog={(v) => {
            setChangeTiresDialog(v);
            if (vehicleIsEdited !== v) setVehicleIsEdited(v);
          }}
          setParts={setParts}
          onCancel={() => {
            if (setNewOrder) setNewOrder(order);
          }}
          additionalLoading={additionalServicesResp.isLoading}
          additionalServices={additionalServices}
          commonPricesParams={commonPricesParams}
        />
      )}
      <div id="parts-container" style={{ display: 'block' }}>
        {parts.map((part) => {
          const isTire = part.ufo_details?.[0]?.subtype === 'TIRE';
          const stockingTireOptions = stockingGroupedParts?.find((gp) => gp.mspn === part.mspn)?.options;
          const isMisc = part.apps?.[0]?.additional_information?.category === 'MISC' ? 'MSC' : '';
          const partId = isMisc ? part.comment : part.id;
          const isPql = part.pql_pricing ? 'PQL' : 'NO-PQL';
          const componentId = `${isMisc}-${getMspn(part) ?? ''}-${partId ?? ''}-${isPql}`;
          return (
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions, @typescript-eslint/no-unsafe-argument
            <div key={hash(part)} id={printMode ? undefined : componentId} style={{ pageBreakInside: 'avoid' }}>
              <EditableParts
                options={stockingOrder && isTire && stockingTireOptions ? stockingTireOptions : undefined}
                installations={installations}
                additionalServices={additionalServices}
                tiresCount={tiresCount}
                key={`editablePart${getMspn(part) ?? ''}`}
                order={order}
                part={isTire ? part : undefined}
                service={isTire ? undefined : service(part)}
                disabled={part.disabled}
                setParts={setPartsFunction}
                setSelected={(p) => setSelectedPart(p, isTire)}
                selected={part.selected === undefined ? true : part.selected}
                isEditing={isEditing}
                smallSize={smallSize}
                printMode={printMode}
              />
            </div>
          );
        })}
      </div>
      {printMode && <hr style={{ width: '100%', marginTop: 16, marginBottom: 16 }} />}
      {!isDealer && areProductsAdded && !additionalServicesResp.error && (
        <Totals
          oldOrder={!!oldOrder}
          order={order}
          printMode={printMode}
          commonPricesParams={commonPricesParams}
          editMode={isEditing || editedOrder}
          componentPlacement={componentPlacement}
        />
      )}
    </>
  );
}
